"use client";

import React, { useEffect, useRef, useState } from "react";

import { Button } from "@/components/ui/primitives/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogOverlay,
  DialogPortal,
} from "@/components/ui/overlays/dialog";
import { Switch } from "@/components/ui/primitives/switch";
import { Badge } from "@/components/ui/primitives/badge";
import { Copy, Link, X, Users } from "lucide-react";
import { toast } from "sonner";
import { switchPublicity } from "@/lib/insightApi";
import { TextShimmer } from "@/components/ui/primitives/text-shimmer";
import { ANALYTICS_EVENTS } from "@/lib/analytics/catalog";
import { trackResult, trackUi } from "@/lib/analytics/amplitude";

// Define interfaces
interface DetailedReportResponse {
  id: string;
  name: string;
  is_public?: boolean;
}

interface ReportShareModalProps {
  isOpen: boolean;
  onClose: () => void;
  report: DetailedReportResponse;
}

export const ReportShareModal: React.FC<ReportShareModalProps> = ({
  isOpen,
  onClose,
  report,
}) => {
  const [isWithPublicLink, setWithPublicLink] = useState(false);
  const wasOpenRef = useRef(false);
  const baseUrl = `${window.location.protocol}//${window.location.host}`;

  const [derivedInputs0, setDerivedInputs0] = useState<readonly unknown[] | null>(null);
  if (derivedInputs0 === null || !Object.is(derivedInputs0[0], report)) {
    setDerivedInputs0([report]);
    (() => {
    if (report) {
      setWithPublicLink(report.is_public || false);
    }

    })();
  }

  const handlePublicLinkToggle = async (checked: boolean) => {
    trackUi(ANALYTICS_EVENTS.UI_REPORT_PUBLIC_LINK_TOGGLE_STARTED, {
      report_id: report.id,
      is_public: checked,
    });
    try {
      // Switch publicity via API
      await switchPublicity(report.id, checked);
      setWithPublicLink(checked);
      trackResult(ANALYTICS_EVENTS.RESULT_REPORT_PUBLIC_LINK_TOGGLE_SUCCESS, {
        report_id: report.id,
        is_public: checked,
      });

      if (checked) {
        await copyLinkToClipboard();
      } else {
        toast.success("Public Link removed");
      }

      // TODO: Update project list when available
      // await fetchProjectList();
    } catch (_error) {
      console.error("Failed to switch publicity", _error);
      trackResult(ANALYTICS_EVENTS.RESULT_REPORT_PUBLIC_LINK_TOGGLE_FAILED, {
        report_id: report.id,
        is_public: checked,
        error_code: _error instanceof Error ? _error.name : "toggle_public_link_failed",
      });
      toast.error("Failed to update link status");
    }
  };

  const copyLinkToClipboard = async () => {
    const linkToCopy = `${baseUrl}/space/records/public/${report.id}`;
    try {
      await navigator.clipboard.writeText(linkToCopy);
      trackUi(ANALYTICS_EVENTS.UI_REPORT_PUBLIC_LINK_COPIED, {
        report_id: report.id,
      });
      toast.success("Public Link Copied");
      // TODO: Track share link copied
      // YMTracker.trackReportShareLinkCopied();
    } catch (_error) {
      console.error("Failed to copy link", _error);
      toast.error("Failed to copy link");
    }
  };

  const handleClose = () => {
    // TODO: Track modal closed
    // YMTracker.trackReportShareModalClosed();
    onClose();
  };

  useEffect(() => {
    if (isOpen && !wasOpenRef.current) {
      trackUi(ANALYTICS_EVENTS.UI_REPORT_SHARE_MODAL_OPENED, {
        report_id: report.id,
        is_public: Boolean(report.is_public),
      });
    }
    wasOpenRef.current = isOpen;
  }, [isOpen, report.id, report.is_public]);

  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      <DialogPortal>
        <DialogOverlay className="backdrop-blur-sm" />
        <DialogContent
          className="bg-[var(--background-secondary)] dark:bg-card !max-w-[580px] max-h-[90vh] border-0 p-5 shadow-2xl rounded-[22px] overflow-hidden transition-all duration-300 ease-in-out"
          style={{
            // backgroundColor: "var(--background-secondary)",
            transform: "translate(0, -20%)",
          }}
          showCloseButton={false}
        >
          <div className="relative p-0 m-0 max-h-[80vh] overflow-y-auto">
            {/* Header */}
            <DialogHeader className="flex flex-col pb-2 gap-0">
              <div className="flex justify-between items-center pb-1">
                <DialogTitle className="text-2xl font-semibold m-0" translate="yes">
                  Share Report
                </DialogTitle>
                <Button
                  onClick={handleClose}
                  variant="ghost"
                  size="icon"
                  className="h-8 w-8"
                >
                  <X className="h-4 w-4" />
                </Button>
              </div>
              <DialogDescription
                className="text-sm m-0"
                translate="yes"
                // style={{ color: "var(--text)", fontSize: "14px" }}
              >
                Decide who can open and view this report.
              </DialogDescription>
            </DialogHeader>

            {/* Divider */}
            <div
              className="w-full my-3 h-px"
              style={
                {
                  // backgroundColor: "var(--report-widget-subtle-background)",
                  // margin: "20px 0",
                }
              }
            />

            {/* Share options */}
            <div
              className={`space-y-6 mx-1 ${isWithPublicLink ? "mb-4" : "mb-8"}`}
            >
              {/* Invite-only section */}
              <div className="flex items-center gap-3">
                <div className="flex h-10 w-10 items-center justify-center rounded-full bg-muted flex-shrink-0">
                  <Users className="h-4 w-4 text-muted-foreground" />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <h3 className="font-semibold text-lg" translate="yes">Invite-only</h3>
                      <Badge variant="default" className="text-xs" translate="yes">
                        soon
                      </Badge>
                    </div>
                    <Switch
                      checked={false}
                      disabled={true}
                      className="data-[state=unchecked]:bg-background"
                    />
                  </div>
                  <p className="text-sm text-muted-foreground" translate="yes">
                    Only people you invite can view the report
                  </p>
                </div>
              </div>

              {/* Public link section */}
              <div className="flex items-center gap-3">
                <div className="flex h-10 w-10 items-center justify-center rounded-full bg-muted flex-shrink-0">
                  <Link className="h-4 w-4 text-muted-foreground" />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center justify-between">
                    <h3 className="font-semibold text-lg" translate="yes">Public link</h3>
                    <Switch
                      checked={isWithPublicLink}
                      onCheckedChange={handlePublicLinkToggle}
                      className="data-[state=unchecked]:bg-background"
                    />
                  </div>
                  <p className="text-sm text-muted-foreground" translate="yes">
                    Anyone with the link can access the{" "}
                    <strong>public view</strong> of the report.
                  </p>
                </div>
              </div>
            </div>

            {/* Hidden parts info */}
            {isWithPublicLink && (
              <div className="mb-3 py-3 px-4 bg-muted dark:bg-muted/30 rounded-2xl transition-all duration-300 ease-in-out animate-in fade-in-0 slide-in-from-top-2" translate="yes">
                <p className="font-medium text-sm mb-2">
                  <strong>Hidden in the public view:</strong>
                </p>
                <ul className="space-y-1 mb-2">
                  <li className="flex items-start gap-2 text-sm">
                    <span className="w-1.5 h-1.5 bg-muted-foreground rounded-full mt-2 flex-shrink-0" />
                    <span>Your name & email</span>
                  </li>
                  <li className="flex items-start gap-2 text-sm">
                    <span className="w-1.5 h-1.5 bg-muted-foreground rounded-full mt-2 flex-shrink-0" />
                    <span>Research objectives</span>
                  </li>
                  <li className="flex items-start gap-2 text-sm">
                    <span className="w-1.5 h-1.5 bg-muted-foreground rounded-full mt-2 flex-shrink-0" />
                    <span>Messages you sent in AI Chat</span>
                  </li>
                </ul>
              </div>
            )}

            {/* Link display and copy button */}
            <div
              className="flex items-center justify-between transition-all duration-300 ease-in-out animate-in fade-in-0 slide-in-from-bottom-2"
              style={{
                display: "flex",
                flexDirection: "row",
                alignItems: "center",
                padding: "6px 6px 6px 18px",
                backgroundColor: "var(--background)",
                borderRadius: "32px",
                fontSize: "16px",
                position: "relative",
                overflow: "hidden",
              }}
            >
              <div
                className="flex-1 min-w-0 mr-4"
                style={{
                  whiteSpace: "nowrap",
                  overflow: "hidden",
                  color:
                    "color-mix(in srgb, var(--text-secondary) 70%, transparent)",
                  marginRight: "16px",
                  position: "relative",
                  flex: 1,
                  minWidth: 0,
                }}
              >
                <TextShimmer
                  className="text-sm font-mono break-all"
                  duration={4}
                  spread={1}
                >
                  {`${baseUrl}/space/records/public/${report.id}`}
                </TextShimmer>
                <div
                  className="absolute right-0 top-0 w-15 h-full"
                  style={{
                    content: '""',
                    position: "absolute",
                    right: "-16px",
                    top: 0,
                    width: "60px",
                    height: "100%",
                    background:
                      "linear-gradient(to right, transparent, var(--background) 100%)",
                  }}
                />
              </div>
              <button
                onClick={isWithPublicLink ? copyLinkToClipboard : undefined}
                disabled={!isWithPublicLink}
                className={`flex items-center gap-2 px-3.5 py-3.5 rounded-full text-sm font-medium whitespace-nowrap flex-shrink-0 min-w-fit transition-colors ${
                  isWithPublicLink
                    ? "hover:opacity-90 active:opacity-80"
                    : "cursor-not-allowed"
                }`}
                style={{
                  backgroundColor: isWithPublicLink
                    ? "var(--primary)"
                    : "var(--background-secondary)",
                  color: isWithPublicLink
                    ? "var(--bw)"
                    : "var(--text-disabled)",
                  fontSize: "16px",
                  border: "none",
                  padding: "14px 18px 14px 14px",
                  borderRadius: "50px",
                  position: "relative",
                  zIndex: 1,
                  whiteSpace: "nowrap",
                  flexShrink: 0,
                  minWidth: "fit-content",
                  cursor: isWithPublicLink ? "pointer" : "not-allowed",
                }}
              >
                <Copy className="h-4 w-4" />
                Copy Link
              </button>
            </div>
          </div>
        </DialogContent>
      </DialogPortal>
    </Dialog>
  );
};

export default ReportShareModal;
